import { useState, useEffect } from 'react';
import { getCurrentUser } from '../services/user.service.js';
import useAuthStore from '../auth.store.js';

/**
 * React hook to get current user data
 * Provides loading state, error handling, and automatic refresh
 * @param forceRefresh - If true, bypasses cache and fetches fresh data
 */ const useCurrentUser = (forceRefresh = false)=>{
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Get user data from store for immediate access
    const storeUser = useAuthStore((state)=>state.user);
    const storeLoading = useAuthStore((state)=>state.userLoading);
    const storeError = useAuthStore((state)=>state.userError);
    useEffect(()=>{
        let isMounted = true;
        const fetchUser = async ()=>{
            try {
                setLoading(true);
                setError(null);
                const currentUser = await getCurrentUser(forceRefresh);
                if (isMounted) {
                    setUser(currentUser);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err);
                }
            } finally{
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        fetchUser();
        return ()=>{
            isMounted = false;
        };
    }, [
        forceRefresh
    ]);
    // Use store data if available and not forcing refresh
    const finalUser = forceRefresh ? user : storeUser || user;
    const finalLoading = forceRefresh ? loading : storeLoading || loading;
    const finalError = forceRefresh ? error : storeError || error;
    return {
        user: finalUser,
        loading: finalLoading,
        error: finalError,
        refetch: ()=>getCurrentUser(true),
        // Convenience methods
        isAuthenticated: !!finalUser,
        userId: finalUser?.userId,
        username: finalUser?.username,
        email: finalUser?.attributes?.email,
        customerId: finalUser?.customerId,
        isFederated: finalUser?.isFederated() ?? false,
        // Password reset flag
        isPasswordResetRequired: finalUser?.payload?.['custom:force_reset_pw'] === 'true'
    };
};

export { useCurrentUser as default, useCurrentUser };
//# sourceMappingURL=useCurrentUser.js.map
